/**
 * Form validation class that handles form error display and cleanup
 */
class FormValidator {
  /**
   * Initialize the FormValidator with default options
   */
  constructor() {
    this.defaultOptions = {
      errorTextClasses: 'text-red-500',
      errorTextClassesCheckRadio: 'text-red-500 absolute bottom-0 left-0',
      errorTextClassesFile: 'text-red-500',
      errorTextClassesSelect: 'text-red-500',
      inputErrorClasses: '!border-red-500',
      selectErrorClasses: '!border-red-500',
      errorTextClassesForm: 'text-red-500',
      defaultErrorMessage: 'This field is required',
      errorClearDelay: 500,
      transitionDelay: 300,
    };
  }

  /**
   * Main validation method that processes form data and displays errors
   * @param {string|object} formData - Form data as string or object
   * @returns {object} Object containing field errors
   */
  clearAllFormErrors(form, options) {
    form.classList.remove('error');
    const formErrorText = form.querySelector('.form-error-text');
    if (formErrorText) {
      formErrorText.remove();
    }
    const errorInputs = form.querySelectorAll('input, textarea, select');
    errorInputs.forEach((input) => {
      input.classList.remove('error', ...options.inputErrorClasses.split(' '));
    });
    const errorContainers = form.querySelectorAll('.error, .error-array-checklist, .meta-upload-file, .btn-upload-input, .meta-select');
    errorContainers.forEach((container) => {
      container.classList.remove('error', 'error-array-checklist');
    });
    const errorTexts = form.querySelectorAll('.error-text');
    errorTexts.forEach((el) => {
      el.remove();
    });
  }

  validate(formData) {
    if (!document.querySelector('.portal')) {
      try {
        console.log(formData);
        const data =
          typeof formData === 'string' ? JSON.parse(formData) : formData;
        console.log(data);

        const formId = data.form_id;
        const errorMessages = data.names;

        let formErrorMessage = '';
        let fieldErrors = {};

        if (
          data?.names &&
          Array.isArray(data.names) &&
          Array.isArray(data.names[0]) &&
          typeof data.names[0][0] === 'string'
        ) {
          formErrorMessage = data.names[0][0];
        } else if (
          data?.names &&
          typeof data.names === 'object' &&
          !Array.isArray(data.names)
        ) {
          fieldErrors = data.names;
        }

        const finalOptions = { ...this.defaultOptions };
        const errors = {};

        const form = document.getElementById(formId);
        if (!form) {
          throw new Error(`Form with ID "${formId}" not found.`);
        }
        this.clearAllFormErrors(form, finalOptions);

        if (formErrorMessage && formErrorMessage !== '') {
          this.handleFormError(form, formErrorMessage, finalOptions);
        }

        if (
          errorMessages &&
          (typeof errorMessages !== 'object' || Array.isArray(errorMessages))
        ) {
          throw new Error('Invalid error format. Expected an object.');
        }

        if (errorMessages) {
          Object.keys(errorMessages).forEach((fieldName) => {
            this.handleFieldError(
              form,
              fieldName,
              errorMessages[fieldName],
              finalOptions,
              errors
            );
          });
        }

        this.updateAllSimpleBars();

        return errors;
      } catch (error) {
        console.error('Error parsing JSON data:', error.message);
        return {};
      }
    }
  }

  /**
   * Handle form-level error display
   * @param {HTMLElement} form - The form element
   * @param {string} errorMessage - Error message to display
   * @param {object} options - Validation options
   */
  handleFormError(form, errorMessage, options) {
    let formErrorElement = form.querySelector('.form-error-text');
    if (formErrorElement) {
      formErrorElement.remove();
    }

    formErrorElement = document.createElement('div');
    formErrorElement.className = `form-error-text ${options.errorTextClassesForm}`;
    formErrorElement.textContent = errorMessage;

    form.classList.add('error');
    form.appendChild(formErrorElement);

    console.log(form);

    if (form.querySelector('input, textarea')) {
      const inputs = form.querySelectorAll('input, textarea');
      inputs.forEach((input) => {
        input.addEventListener('click', () => {
          form.classList.remove('error');
        });

        input.addEventListener('change', () => {
          form.classList.remove('error');
        });
      });
    }
  }

  /**
   * Handle field-level error display and setup
   * @param {HTMLElement} form - The form element
   * @param {string} fieldName - Name of the field with error
   * @param {array} fieldError - Error data for the field
   * @param {object} options - Validation options
   * @param {object} errors - Errors object to populate
   */
  handleFieldError(form, fieldName, fieldError, options, errors) {
    let inputField = this.getFirstFieldElement(form.elements[fieldName]);

    if (!inputField) {
      inputField = form.querySelector(`[name="${fieldName}[]"]`);
    }

    console.log(fieldName, inputField);

    if (!inputField) {
      console.warn(`Input field "${fieldName}" not found in form.`);
      return;
    }

    const parentElement = inputField.parentElement;
    const isCheckboxOrRadio =
      inputField.type === 'checkbox' || inputField.type === 'radio';
    const choiceGroupWrapper = isCheckboxOrRadio
      ? this.getChoiceGroupWrapper(inputField)
      : null;
    const isFile =
      inputField.type === 'file' ||
      inputField.name === 'cv_field' ||
      !!inputField.closest('.meta-upload-file, .btn-upload-input');
    const fileWrapper = isFile
      ? this.getFileWrapper(inputField, parentElement)
      : null;
    const isSelect = inputField.tagName.toLowerCase() === 'select';
    const selectWrapper = isSelect
      ? inputField.closest('.meta-select') || parentElement
      : null;

    const errorMessage = fieldError?.[0] || options.defaultErrorMessage;

    const errorTextClass = this.getErrorTextClass(
      isFile,
      isCheckboxOrRadio,
      isSelect,
      options
    );

    let errorTextElement = this.getErrorTextElement(
      isSelect,
      parentElement,
      selectWrapper,
      fileWrapper,
      choiceGroupWrapper
    );

    if (!isCheckboxOrRadio && !isFile && !isSelect) {
      this.handleRegularFieldError(
        inputField,
        parentElement,
        errorTextElement,
        errorMessage,
        errorTextClass,
        options,
        errors,
        fieldName
      );
    } else if (isCheckboxOrRadio || isFile || isSelect) {
      this.handleSpecialFieldError(
        inputField,
        parentElement,
        errorTextElement,
        errorMessage,
        errorTextClass,
        options,
        errors,
        fieldName,
        isCheckboxOrRadio,
        isFile,
        isSelect,
        selectWrapper,
        fileWrapper,
        choiceGroupWrapper
      );
    }

    this.handleMetaUploadFile(inputField);
    this.addFieldEventListeners(
      inputField,
      parentElement,
      errorTextElement,
      options
    );

    if (choiceGroupWrapper) {
      this.addChoiceGroupEventListeners(
        inputField,
        choiceGroupWrapper,
        errorTextElement,
        options
      );
    }
  }

  getFirstFieldElement(field) {
    if (!field) {
      return null;
    }

    if (field.nodeType === 1) {
      return field;
    }

    if (typeof field.length === 'number' && field.length > 0) {
      if (typeof field.item === 'function') {
        return field.item(0);
      }

      return field[0] || null;
    }

    return null;
  }

  getChoiceGroupWrapper(inputField) {
    return inputField.closest('.meta-checklist, .form-sec__checklist');
  }

  /**
   * Get appropriate CSS class for error text based on field type
   * @param {boolean} isFile - Whether the field is a file input
   * @param {boolean} isCheckboxOrRadio - Whether the field is checkbox or radio
   * @param {boolean} isSelect - Whether the field is a select
   * @param {object} options - Validation options
   * @returns {string} CSS class for error text
   */
  getErrorTextClass(isFile, isCheckboxOrRadio, isSelect, options) {
    if (isFile) {
      return options.errorTextClassesFile;
    } else if (isCheckboxOrRadio) {
      return options.errorTextClassesCheckRadio;
    } else if (isSelect) {
      return options.errorTextClassesSelect;
    } else {
      return options.errorTextClasses;
    }
  }

  /**
   * Find existing error text element based on field type
   * @param {boolean} isSelect - Whether the field is a select
   * @param {HTMLElement} parentElement - Parent element of the field
   * @returns {HTMLElement|null} Existing error text element or null
   */
  getErrorTextElement(
    isSelect,
    parentElement,
    selectWrapper = null,
    fileWrapper = null,
    choiceGroupWrapper = null
  ) {
    if (fileWrapper) {
      return fileWrapper.querySelector('.error-text');
    }

    if (choiceGroupWrapper) {
      return choiceGroupWrapper.querySelector('.error-text');
    }

    if (isSelect) {
      return selectWrapper?.querySelector('.error-text') || null;
    }

    return parentElement.querySelector('.error-text');
  }

  getFileWrapper(inputField, parentElement) {
    return (
      inputField.closest('.meta-upload-file') ||
      inputField.closest('.btn-upload-input') ||
      parentElement.closest('.meta-upload-file') ||
      parentElement.closest('.btn-upload-input') ||
      parentElement
    );
  }

  /**
   * Handle regular input field error display
   * @param {HTMLElement} inputField - The input field element
   * @param {HTMLElement} parentElement - Parent element of the field
   * @param {HTMLElement} errorTextElement - Error text element
   * @param {string} errorMessage - Error message to display
   * @param {string} errorTextClass - CSS class for error text
   * @param {object} options - Validation options
   * @param {object} errors - Errors object
   * @param {string} fieldName - Name of the field
   */
  handleRegularFieldError(
    inputField,
    parentElement,
    errorTextElement,
    errorMessage,
    errorTextClass,
    options,
    errors,
    fieldName
  ) {
    errors[fieldName] = errorMessage;

    inputField.classList.add('error', ...options.inputErrorClasses.split(' '));

    if (!errorTextElement) {
      errorTextElement = document.createElement('span');
      errorTextElement.className = `error-text ${errorTextClass}`;
      parentElement.appendChild(errorTextElement);
    }
    errorTextElement.textContent = errorMessage;

    this.addErrorTextEventListener(
      errorTextElement,
      inputField,
      parentElement,
      options
    );
  }

  /**
   * Handle special field error display (checkbox, radio, file, select)
   * @param {HTMLElement} inputField - The input field element
   * @param {HTMLElement} parentElement - Parent element of the field
   * @param {HTMLElement} errorTextElement - Error text element
   * @param {string} errorMessage - Error message to display
   * @param {string} errorTextClass - CSS class for error text
   * @param {object} options - Validation options
   * @param {object} errors - Errors object
   * @param {string} fieldName - Name of the field
   * @param {boolean} isCheckboxOrRadio - Whether the field is checkbox or radio
   * @param {boolean} isFile - Whether the field is a file input
   * @param {boolean} isSelect - Whether the field is a select
   */
  handleSpecialFieldError(
    inputField,
    parentElement,
    errorTextElement,
    errorMessage,
    errorTextClass,
    options,
    errors,
    fieldName,
    isCheckboxOrRadio,
    isFile,
    isSelect,
    selectWrapper = null,
    fileWrapper = null,
    choiceGroupWrapper = null
  ) {
    errors[fieldName] = errorMessage;

    inputField.classList.add('error');

    if (isFile) {
      fileWrapper?.classList.add('error');
    } else if (isCheckboxOrRadio) {
      if (choiceGroupWrapper) {
        choiceGroupWrapper.classList.add('error', 'error-array-checklist');
      } else {
        parentElement.classList.add('error');
      }
    } else if (isSelect) {
      selectWrapper?.classList.add('error');
    }

    if (!errorTextElement) {
      errorTextElement = document.createElement('span');
      errorTextElement.className = `error-text ${errorTextClass}`;

      if (isFile) {
        const fileErrorContainer = fileWrapper || parentElement;
        const errorTextsArr =
          fileErrorContainer.querySelectorAll('.error-text');
        if (errorTextsArr.length) {
          errorTextsArr.forEach((el) => {
            el.remove();
          });
        }

        const metaUploadInput =
          fileErrorContainer.querySelector('.meta-upload-file__input');
        if (metaUploadInput) {
          metaUploadInput.insertAdjacentElement('afterend', errorTextElement);
        } else {
          fileErrorContainer.appendChild(errorTextElement);
        }
      } else if (isSelect) {
        if (selectWrapper) {
          selectWrapper.appendChild(errorTextElement);
        } else {
          parentElement.appendChild(errorTextElement);
        }
      } else if (choiceGroupWrapper) {
        choiceGroupWrapper.appendChild(errorTextElement);
      } else {
        parentElement.appendChild(errorTextElement);
      }
    }
    errorTextElement.textContent = errorMessage;

    if (choiceGroupWrapper || parentElement.closest('.error-array-checklist')) {
      const checkListArray =
        choiceGroupWrapper || inputField.closest('.error-array-checklist');

      const errorTextsArr = checkListArray.querySelectorAll('.error-text');
      if (errorTextsArr.length) {
        for (let i = 0; i < errorTextsArr.length; i++) {
          errorTextsArr[i].remove();
        }
      }

      checkListArray.appendChild(errorTextElement);
      errorTextElement.classList.remove(
        ...options.errorTextClassesCheckRadio.split(' ')
      );
      errorTextElement.classList.add(
        'w-full',
        ...options.errorTextClasses.split(' ')
      );

      this.addErrorChecklistArrayEventListener(
        errorTextElement,
        inputField,
        parentElement,
        options,
        fieldName
      );
    }

    this.addErrorTextEventListener(
      errorTextElement,
      inputField,
      parentElement,
      options
    );
  }

  /**
   * Handle meta upload file error styling
   * @param {HTMLElement} inputField - The input field element
   */
  handleMetaUploadFile(inputField) {
    const fileWrapper = this.getFileWrapper(inputField, inputField.parentElement);
    if (fileWrapper && fileWrapper.matches('.meta-upload-file, .btn-upload-input')) {
      fileWrapper.classList.add('error');
    }
  }

  addErrorChecklistArrayEventListener(
    errorTextElement,
    inputField,
    parentElement,
    options,
    fieldName
  ) {
    const checkListArray = inputField.closest('.error-array-checklist');

    if (checkListArray) {
      const groupName = inputField.name;
      const checkListArrayElements = checkListArray.querySelectorAll(
        `[name="${groupName}"]`
      );

      const errorTextHandler = () => {
        setTimeout(() => {
          this.clearFieldError(
            inputField,
            checkListArray,
            errorTextElement,
            options
          );

          setTimeout(() => {
            const errorTextsArr =
              checkListArray.querySelectorAll('.error-text');
            if (errorTextsArr.length) {
              for (let i = 0; i < errorTextsArr.length; i++) {
                errorTextsArr[i].remove();
              }
            }

            errorTextElement.removeEventListener('click', errorTextHandler);
            checkListArrayElements.forEach((el) => {
              el.removeEventListener('change', errorTextHandler);
            });
          }, options.errorClearDelay);
        }, options.errorClearDelay);
      };

      errorTextElement.addEventListener('click', errorTextHandler);

      checkListArrayElements.forEach((el) => {
        el.addEventListener('change', (e) => {
          errorTextHandler();
        });
      });
    }
  }

  addChoiceGroupEventListeners(
    inputField,
    choiceGroupWrapper,
    errorTextElement,
    options
  ) {
    const oldGroupHandler = choiceGroupWrapper._validationChoiceGroupHandler;
    if (oldGroupHandler) {
      const oldGroupName = choiceGroupWrapper._validationChoiceGroupName;
      if (oldGroupName) {
        choiceGroupWrapper
          .querySelectorAll(`[name="${oldGroupName}"]`)
          .forEach((el) => {
            el.removeEventListener('change', oldGroupHandler);
            el.removeEventListener('click', oldGroupHandler);
          });
      }
    }

    const groupName = inputField.name;
    const groupHandler = () => {
      setTimeout(() => {
        this.clearFieldError(
          inputField,
          choiceGroupWrapper,
          errorTextElement,
          options
        );
      }, options.errorClearDelay);
    };

    choiceGroupWrapper
      .querySelectorAll(`[name="${groupName}"]`)
      .forEach((el) => {
        el.addEventListener('change', groupHandler);
        el.addEventListener('click', groupHandler);
      });

    choiceGroupWrapper._validationChoiceGroupHandler = groupHandler;
    choiceGroupWrapper._validationChoiceGroupName = groupName;
  }

  /**
   * Add event listeners to error text element for error clearing
   * @param {HTMLElement} errorTextElement - Error text element
   * @param {HTMLElement} inputField - The input field element
   * @param {HTMLElement} parentElement - Parent element of the field
   * @param {object} options - Validation options
   */
  addErrorTextEventListener(
    errorTextElement,
    inputField,
    parentElement,
    options
  ) {
    const oldErrorTextHandler = errorTextElement._validationErrorTextHandler;
    if (oldErrorTextHandler) {
      errorTextElement.removeEventListener('click', oldErrorTextHandler);
    }

    const errorTextHandler = () => {
      setTimeout(() => {
        this.clearFieldError(
          inputField,
          parentElement,
          errorTextElement,
          options
        );
      }, options.errorClearDelay);
    };

    errorTextElement.addEventListener('click', errorTextHandler);
    errorTextElement._validationErrorTextHandler = errorTextHandler;
  }

  /**
   * Add event listeners to field and parent elements for error clearing
   * @param {HTMLElement} inputField - The input field element
   * @param {HTMLElement} parentElement - Parent element of the field
   * @param {HTMLElement} errorTextElement - Error text element
   * @param {object} options - Validation options
   */
  addFieldEventListeners(inputField, parentElement, errorTextElement, options) {
    const oldClickHandler = inputField._validationClickHandler;
    if (oldClickHandler) {
      inputField.removeEventListener('click', oldClickHandler);
    }

    const clickHandler = () => {
      setTimeout(() => {
        this.clearFieldError(
          inputField,
          parentElement,
          errorTextElement,
          options
        );
      }, options.errorClearDelay);
    };

    inputField.addEventListener('click', clickHandler);
    inputField._validationClickHandler = clickHandler;

    if (parentElement.parentElement) {
      const oldParentClickHandler =
        parentElement.parentElement._validationParentClickHandler;
      if (oldParentClickHandler) {
        parentElement.parentElement.removeEventListener(
          'click',
          oldParentClickHandler
        );
      }

      /*const parentClickHandler = () => {
        setTimeout(() => {
          this.clearParentError(inputField, parentElement, options);
        }, options.errorClearDelay);
      };

      parentElement.parentElement.addEventListener('click', parentClickHandler);
      parentElement.parentElement._validationParentClickHandler =
        parentClickHandler;*/
    }

    const oldDirectParentClickHandler =
      parentElement._validationDirectParentClickHandler;
    if (oldDirectParentClickHandler) {
      parentElement.removeEventListener('click', oldDirectParentClickHandler);
    }

    const directParentClickHandler = () => {
      setTimeout(() => {
        this.clearParentError(inputField, parentElement, options);
      }, options.errorClearDelay);
    };

    parentElement.addEventListener('click', directParentClickHandler);
    parentElement._validationDirectParentClickHandler =
      directParentClickHandler;
  }

  /**
   * Clear field error styling and remove error elements
   * @param {HTMLElement} inputField - The input field element
   * @param {HTMLElement} parentElement - Parent element of the field
   * @param {HTMLElement} errorTextElement - Error text element
   * @param {object} options - Validation options
   */
  clearFieldError(inputField, parentElement, errorTextElement, options) {
    inputField.classList.remove(
      'error',
      ...options.inputErrorClasses.split(' ')
    );
    parentElement.classList.remove('error', 'error-array-checklist');
    inputField.closest('form').classList.remove('error');

    const fileWrapper = this.getFileWrapper(inputField, parentElement);

    if (fileWrapper && fileWrapper.matches('.meta-upload-file, .btn-upload-input')) {
      fileWrapper.classList.remove('error');
      const fileErrorTextElements = fileWrapper.querySelectorAll('.error-text');
      fileErrorTextElements.forEach((el) => {
        el.remove();
      });
    }

    const errorTextElements = parentElement.querySelectorAll('.error-text');
    errorTextElements.forEach((el) => {
      if (el.parentElement) {
        el.parentElement.classList.remove('error');
      }
      el.remove();
    });

    if (inputField.tagName.toLowerCase() === 'select') {
      const selectWrapper = inputField.closest('.meta-select');
      if (selectWrapper) {
        const selectErrorElements =
          selectWrapper.querySelectorAll('.error-text');
        selectErrorElements.forEach((el) => {
          if (el.parentElement) {
            el.parentElement.classList.remove('error');
          }
          el.remove();
        });
        selectWrapper.classList.remove('error');
      }
    }

    if (inputField.name === 'cv_field') {
      const nextSibling = parentElement.nextElementSibling;
      if (nextSibling && nextSibling.classList.contains('error-text')) {
        nextSibling.remove();
      }
    }
  }

  /**
   * Clear parent error styling and remove error elements
   * @param {HTMLElement} inputField - The input field element
   * @param {HTMLElement} parentElement - Parent element of the field
   * @param {object} options - Validation options
   */
  clearParentError(inputField, parentElement, options) {
    parentElement.classList.remove('error', 'error-array-checklist');
    inputField.closest('form').classList.remove('error');

    const fileWrapper = this.getFileWrapper(inputField, parentElement);

    if (fileWrapper && fileWrapper.matches('.meta-upload-file, .btn-upload-input')) {
      fileWrapper.classList.remove('error');
      const errorTextsArr = fileWrapper.querySelectorAll('.error-text');
      if (errorTextsArr.length) {
        errorTextsArr.forEach((el) => {
          el.remove();
        });
      }
    }

    const errorTextElements = parentElement.querySelectorAll('.error-text');
    errorTextElements.forEach((el) => {
      if (el.parentElement) {
        el.parentElement.classList.remove('error');
      }
      el.remove();
    });

    if (inputField.tagName.toLowerCase() === 'select') {
      const selectWrapper = inputField.closest('.meta-select');
      if (selectWrapper) {
        const selectErrorElements =
          selectWrapper.querySelectorAll('.error-text');
        selectErrorElements.forEach((el) => {
          if (el.parentElement) {
            el.parentElement.classList.remove('error');
          }
          el.remove();
        });
        selectWrapper.classList.remove('error');
      }
    }

    if (inputField.name === 'cv_field') {
      const nextSibling = parentElement.nextElementSibling;
      if (nextSibling && nextSibling.classList.contains('error-text')) {
        nextSibling.remove();
      }
    }
  }

  /**
   * Update all SimpleBar instances to recalculate their dimensions
   */
  updateAllSimpleBars() {
    document.querySelectorAll('.simplebar-content').forEach((content) => {
      const simpleBarInstance = SimpleBar.instances.get(
        content.closest('[data-simplebar]')
      );
      if (simpleBarInstance) {
        simpleBarInstance.recalculate();
      }
    });
  }
}

/**
 * Global form validation instance
 */
const formValidator = new FormValidator();

/**
 * Global form validation function for backward compatibility
 * @param {string|object} formData - Form data as string or object
 * @returns {object} Object containing field errors
 */
window.formValidation = function (formData) {
  return formValidator.validate(formData);
};
